import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LARGE_PREVIEW_BYTES, categoryLabel, downloadFile, fetchFileBlob, fmtDate, fmtSize, previewKind } from "./fileUtils";

function PreviewBody({ kind, url, text, file }) {
  if (kind === "pdf") {
    return <iframe src={url} title={file.filename} className="w-full h-full rounded border border-zinc-200 bg-white"
                   data-testid="file-preview-pdf" />;
  }
  if (kind === "image") {
    return (
      <div className="w-full h-full overflow-auto flex items-start justify-center bg-zinc-50 rounded">
        <img src={url} alt={file.filename} className="max-w-full h-auto" data-testid="file-preview-image" />
      </div>
    );
  }
  return (
    <pre className="w-full h-full overflow-auto text-xs bg-zinc-50 rounded p-3 whitespace-pre-wrap"
         data-testid="file-preview-text">{text}</pre>
  );
}

function LargeFileNotice({ file, onConfirm, onDownload, onCancel }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 text-center" data-testid="file-preview-large-warning">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm font-medium text-zinc-800">
        This file is large ({fmtSize(file.size)})
      </div>
      <p className="text-xs text-zinc-500 max-w-sm">
        Previewing files over {LARGE_PREVIEW_BYTES / 1024 / 1024} MB can slow the page down. Download it instead, or preview anyway.
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="file-preview-large-cancel">Cancel</Button>
        <Button size="sm" variant="outline" onClick={onDownload} data-testid="file-preview-large-download">
          <Download className="h-4 w-4 mr-1.5" /> Download instead
        </Button>
        <Button size="sm" onClick={onConfirm} data-testid="file-preview-large-confirm">Preview anyway</Button>
      </div>
    </div>
  );
}

export function FilePreviewDialog({ file, onClose }) {
  const kind = previewKind(file);
  const [confirmed, setConfirmed] = useState(() => (file.size || 0) <= LARGE_PREVIEW_BYTES);
  const [url, setUrl] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const ready = Boolean(kind) && confirmed;

  useEffect(() => {
    if (!ready) return undefined;
    let objUrl = null;
    let alive = true;
    fetchFileBlob(file)
      .then(async (blob) => {
        if (!alive) return;
        if (kind === "text") setText(await blob.text());
        objUrl = URL.createObjectURL(blob);
        setUrl(objUrl);
      })
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message || "Could not load file"));
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [file, kind, ready]);

  const download = () => downloadFile(file).catch((e) => toast.error("Download failed", { description: e.message }));
  const meta = [categoryLabel(file.category), fmtDate(file.document_date || file.created_at)].filter(Boolean);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] h-[88vh] flex flex-col gap-3" data-testid="file-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-8" data-testid="file-preview-title">{file.filename}</DialogTitle>
          <DialogDescription className="truncate">
            {meta.join(" · ")}{file.description ? ` — ${file.description}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0">
          {!kind && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-zinc-600"
                 data-testid="file-preview-unsupported">
              This file type can't be previewed in the browser.
              <Button size="sm" onClick={download}><Download className="h-4 w-4 mr-1.5" /> Download</Button>
            </div>
          )}
          {kind && !confirmed && (
            <LargeFileNotice file={file} onConfirm={() => setConfirmed(true)} onCancel={onClose}
                             onDownload={() => { download(); onClose(); }} />
          )}
          {ready && error && <div className="text-sm text-red-700" data-testid="file-preview-error">{error}</div>}
          {ready && !error && !url && (
            <div className="h-full flex items-center justify-center text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading preview…
            </div>
          )}
          {ready && url && <PreviewBody kind={kind} url={url} text={text} file={file} />}
        </div>
        <div className={`flex justify-end gap-2 ${kind && !confirmed ? "hidden" : ""}`}>
          {url && (
            <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")}
                    data-testid="file-preview-open-tab">
              <ExternalLink className="h-4 w-4 mr-1.5" /> Open in new tab
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={download} data-testid="file-preview-download">
            <Download className="h-4 w-4 mr-1.5" /> Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
